
import { useState, useRef, useEffect } from 'react';
import mapboxgl from 'mapbox-gl';
import { toast } from '@/components/ui/use-toast';
import { MeshLocation } from '@/components/security/meshLocationsData';

export const useMapboxInit = (
  mapboxToken: string,
  locations: MeshLocation[],
  mapStyle: string = 'mapbox://styles/mapbox/light-v11'
) => {
  const mapContainer = useRef<HTMLDivElement>(null);
  const map = useRef<mapboxgl.Map | null>(null);
  const [mapLoaded, setMapLoaded] = useState(false);
  const [mapInitialized, setMapInitialized] = useState(false);
  const [mapError, setMapError] = useState<string | null>(null);

  useEffect(() => {
    if (!mapContainer.current || mapInitialized) return;
    
    console.log('Initializing map with token starting with:', mapboxToken.substring(0, 10) + '...');
    
    if (!mapboxToken || mapboxToken.trim() === '') {
      console.error('Mapbox token is missing or empty');
      setMapError('Missing Mapbox token');
      toast({
        title: "Map Error",
        description: "Invalid Mapbox token. Please check your token and try again.",
        variant: "destructive",
      });
      return;
    }

    try {
      mapboxgl.accessToken = mapboxToken;
      
      map.current = new mapboxgl.Map({
        container: mapContainer.current,
        style: mapStyle,
        center: [0, 20],
        zoom: 1.5,
        attributionControl: false
      });

      map.current.on('load', () => {
        if (!map.current) return;
        console.log('Map loaded successfully');
        
        const bounds = new mapboxgl.LngLatBounds();
        locations.forEach(location => {
          bounds.extend([location.lng, location.lat]);
        });
        
        if (!bounds.isEmpty()) {
          map.current.fitBounds(bounds, {
            padding: 50,
            maxZoom: 2.5
          });
        }
        
        setMapLoaded(true);
        setMapError(null);
        toast({
          title: "Map Loaded",
          description: "Security map has been loaded successfully.",
        });
      });

      map.current.addControl(
        new mapboxgl.NavigationControl(),
        'bottom-right'
      );

      map.current.on('error', (e) => {
        console.error('Mapbox error:', e.error);
        setMapError(e.error.message || 'Unknown map error');
        toast({
          title: "Map Error",
          description: `Error loading map: ${e.error.message || 'Unknown error'}`,
          variant: "destructive",
        });
      });

      setMapInitialized(true);
    } catch (error) {
      console.error('Error initializing Mapbox map:', error);
      setMapError(error instanceof Error ? error.message : 'Unknown initialization error');
      toast({
        title: "Map Initialization Error",
        description: `Failed to initialize map: ${error instanceof Error ? error.message : 'Unknown error'}`,
        variant: "destructive",
      });
    }

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
        setMapInitialized(false);
        console.log('Map removed during cleanup');
      }
    };
  }, [mapboxToken, locations, mapStyle]);

  return {
    mapContainer,
    map,
    mapLoaded,
    mapError
  };
};
